import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { User } from 'dashboard/types';
import type AnalyticsService from 'dashboard/services/analytics';
import type CurrentUserService from 'dashboard/services/current-user';
import type RouterService from '@ember/routing/router-service';
import type SessionService from 'dashboard/services/-session';
import { update } from '@intercom/messenger-js-sdk';
import type { IntlService } from 'ember-intl';
import { NoAccessError } from 'dashboard/utils/errors';
import { PREVIOUS_AUTH_ERROR_KEY } from 'dashboard/constants';
import window from 'ember-window-mock';
import type Transition from '@ember/routing/transition';
import { isTesting, macroCondition } from '@embroider/macros';

/**
 * This is the authenticated portion of the application. Unauthenticated users
 * will be directed to the login screen.
 * All routes beneath this one can assume that the current user is loaded along
 * with their list of administered purchasers/accounts.
 *
 * @module DashboardRoute
 */
export default class DashboardRoute extends Route {
  @service declare session: SessionService;
  @service declare currentUser: CurrentUserService;
  @service declare intl: IntlService;
  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;

  constructor(...args: []) {
    super(...args);

    this.router.on('routeDidChange', () => {
      window.scroll(0, 0);
    });
  }

  async beforeModel(transition: Transition): Promise<void> {
    this.session.requireAuthentication(transition, 'login');
    try {
      await this.currentUser.load();
      const user: User = this.currentUser.user;

      // ignore Intercom and Analytics setup in BlingMode
      if (this.session.isBlingMode) {
        return;
      }

      if (macroCondition(!isTesting())) {
        try {
          update({
            user_id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            user_hash: this.session.data?.authenticated.intercomHMAC,
          });

          this.analytics.analytics.identify(user.id, {
            localePref: user.locale,
          });
        } catch {
          // console.error(err);
        }
      }
    } catch (err) {
      if (err instanceof NoAccessError) {
        /**
         * Stash NoAccessError errors so that the login page can show an appropriate
         * error message when the app reboots after session invalidation.
         */
        try {
          window.sessionStorage.setItem(
            PREVIOUS_AUTH_ERROR_KEY,
            err.toString()
          );
        } catch (storageError) {
          console.error('Failed to access sessionStorage:', storageError);
        }
      }
      transition.abort();
      this.session.invalidate();
    }
  }
}
